import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MatSnackBar } from '@angular/material/snack-bar';
import { StudyShare, StudyShareService } from '../../../services/study-share.service';

@Component({
  selector: 'app-study-reshare-pending',
  templateUrl: './study-reshare-pending.component.html',
  styleUrls: ['./study-reshare-pending.component.scss'],
  standalone: false,
})
export class StudyResharePendingComponent implements OnInit, OnDestroy {
  @Input() hubFacilityId!: string;
  /** Emits the count of pending (re_requested) requests whenever it changes. */
  @Output() pendingCount = new EventEmitter<number>();

  pendingRequests: StudyShare[] = [];
  requestHistory: StudyShare[] = [];
  approvingIds = new Set<string>();
  rejectingIds = new Set<string>();
  expandedShareId: string | null = null;

  private destroy$ = new Subject<void>();

  constructor(
    private studyShareService: StudyShareService,
    private snackBar: MatSnackBar,
  ) {}

  ngOnInit(): void {
    this.studyShareService.watchSharesSentByFacility(this.hubFacilityId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: shares => this.handleShares(shares),
        error: err => console.error('[StudyResharePending] watch failed:', err),
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private handleShares(shares: StudyShare[]): void {
    const requestShares = shares
      .filter(s => s.status === 're_requested' || s.status === 'rejected' || s.status === 'reshared')
      .sort((a, b) => {
        const aTime = a.re_request_sent_at?.toDate().getTime() ?? 0;
        const bTime = b.re_request_sent_at?.toDate().getTime() ?? 0;
        return bTime - aTime;
      });

    this.pendingRequests = requestShares.filter(s => s.status === 're_requested');
    this.requestHistory = requestShares.filter(s => s.status !== 're_requested');
    this.pendingCount.emit(this.pendingRequests.length);
  }

  toggleDetails(shareId: string): void {
    this.expandedShareId = this.expandedShareId === shareId ? null : shareId;
  }

  statusLabel(share: StudyShare): string {
    if (share.status === 'reshared') return 'Approved';
    if (share.status === 'rejected') return 'Rejected';
    return share.status;
  }

  statusClass(share: StudyShare): string {
    if (share.status === 'reshared') return 'status--approved';
    if (share.status === 'rejected') return 'status--rejected';
    return '';
  }

  formatDate(ts: { toDate(): Date } | undefined): string {
    if (!ts) return '—';
    return ts.toDate().toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit', hour12: true,
    });
  }

  /** Parse DICOM patient name (LAST^FIRST^MIDDLE or LAST, FIRST) to readable format. */
  formatPatientName(name?: string): string {
    if (!name) return '';
    if (name.includes('^')) {
      const parts = name.split('^');
      const nameParts = [parts[1], parts[2], parts[0]].filter(p => (p || '').trim() !== '');
      return nameParts.join(' ');
    }
    if (name.includes(',')) {
      const [last, first] = name.split(',', 2);
      return [first?.trim(), last?.trim()].filter(Boolean).join(' ');
    }
    return name;
  }

  onApprove(share: StudyShare): void {
    if (this.approvingIds.has(share.id)) return;
    this.approvingIds.add(share.id);

    this.studyShareService.approveReshare(share.id)
      .then(() => {
        this.snackBar.open(
          `Re-share approved. Please re-run C-MOVE to push the studies to ${share.shared_to_facility_id}.`,
          'OK',
          { duration: 8000 }
        );
      })
      .catch(err => {
        console.error('[StudyResharePending] approveReshare failed:', err);
        this.snackBar.open('Failed to approve re-share. Please try again.', 'Dismiss', { duration: 6000 });
      })
      .finally(() => {
        this.approvingIds.delete(share.id);
      });
  }

  onReject(share: StudyShare): void {
    if (this.rejectingIds.has(share.id)) return;
    this.rejectingIds.add(share.id);

    this.studyShareService.rejectReshare(share.id)
      .then(() => {
        this.snackBar.open(
          `Re-share request rejected. ${share.shared_to_facility_id} has been notified.`,
          'OK',
          { duration: 6000 }
        );
      })
      .catch(err => {
        console.error('[StudyResharePending] rejectReshare failed:', err);
        this.snackBar.open('Failed to reject re-share. Please try again.', 'Dismiss', { duration: 6000 });
      })
      .finally(() => {
        this.rejectingIds.delete(share.id);
      });
  }
}
